import { useEffect, useState, useCallback, useRef } from "react";
import apiClient from "utils/apiClient";
import { PM_SERVICE_BASE_URL } from "constants";

const hasAuthToken = () =>
  typeof sessionStorage !== "undefined" && !!sessionStorage.getItem("authToken");

const PAGE_SIZE = 100;
const PROFILE_BASE_URL = "/profile-service/api/v1/user-profiles";

const getTaskAssigneeId = (task = {}) => {
  const id = task?.assignee?.id || task?.assigneeId || task?.assignee_id || null;
  return id ? String(id) : null;
};

const normalizeAssigneeProfile = (profile = {}, fallbackId = null) => {
  const firstName = profile?.firstName || "";
  const lastName = profile?.lastName || "";
  const fullName =
    String(profile?.fullName || "").trim() ||
    `${firstName} ${lastName}`.trim() ||
    profile?.username ||
    null;

  return {
    id: profile?.id || fallbackId || null,
    email:
      profile?.email ||
      profile?.contactDetails?.personalEmail ||
      profile?.contactDetails?.workEmail ||
      null,
    firstName,
    lastName,
    fullName,
    username: profile?.username || null,
    imageUrl: profile?.imageUrl || profile?.profilePhoto || null,
  };
};

export const useKanbanTasks = (projectId, iterationId) => {
  const [tasks, setTasks] = useState([]);
  const [pageByStatus, setPageByStatus] = useState({});
  const [hasMoreByStatus, setHasMoreByStatus] = useState({});
  const [loadingByStatus, setLoadingByStatus] = useState({});

  const pageRef = useRef({});
  const loadingRef = useRef({});
  const assigneeCacheRef = useRef(new Map());

  const hydrateTaskAssignee = useCallback((task = {}) => {
    const assigneeId = getTaskAssigneeId(task);
    if (!assigneeId) return task;

    const profile = assigneeCacheRef.current.get(assigneeId);
    if (!profile) return task;
    const fallbackFullName =
      task?.assignee?.fullName ||
      `${task?.assignee?.firstName || ""} ${task?.assignee?.lastName || ""}`.trim() ||
      null;

    return {
      ...task,
      assigneeId: profile.id || task.assigneeId || task.assignee_id || assigneeId,
      assignee: {
        ...(task.assignee || {}),
        id: profile.id || task?.assignee?.id || assigneeId,
        email: profile.email || task?.assignee?.email || null,
        firstName: profile.firstName || task?.assignee?.firstName || "",
        lastName: profile.lastName || task?.assignee?.lastName || "",
        fullName:
          profile.fullName ||
          fallbackFullName,
        username: profile.username || task?.assignee?.username || null,
        imageUrl:
          profile.imageUrl !== null && profile.imageUrl !== undefined
            ? profile.imageUrl
            : task?.assignee?.imageUrl || null,
      },
      assigneeName: profile.fullName || fallbackFullName,
    };
  }, []);

  const refetchAssigneeProfiles = useCallback(async (tasksList = []) => {
    const uniqueIds = [
      ...new Set((tasksList || []).map(getTaskAssigneeId).filter(Boolean)),
    ];

    const idsToFetch = uniqueIds.filter((id) => !assigneeCacheRef.current.has(id));
    if (idsToFetch.length === 0) return;

    const results = await Promise.allSettled(
      idsToFetch.map((id) =>
        apiClient.get(`${PROFILE_BASE_URL}/${id}?refresh=true`),
      ),
    );

    results.forEach((result, index) => {
      const assigneeId = idsToFetch[index];
      if (result.status === "fulfilled") {
        assigneeCacheRef.current.set(
          assigneeId,
          normalizeAssigneeProfile(result.value?.data, assigneeId),
        );
      } else {
        // Prevent repeated failing requests for missing/deleted users.
        assigneeCacheRef.current.set(assigneeId, null);
      }
    });
  }, []);

  const fetchTasksForColumn = useCallback(
    async (status) => {
      if (loadingRef.current[status]) return;
      if (!hasAuthToken()) {
        setTasks([]);
        setHasMoreByStatus((prev) => ({ ...prev, [status]: false }));
        return;
      }
      const page = pageRef.current[status] || 0;

      try {
        loadingRef.current[status] = true;
        setLoadingByStatus((prev) => ({ ...prev, [status]: true }));

        const res = await apiClient.get(
          `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks`,
          {
            params: {
              status,
              iterationId,
              page,
              size: PAGE_SIZE,
              sort: "position,asc",
            },
          },
        );

        const newTasks = Array.isArray(res?.data?.content) ? res.data.content : [];
        await refetchAssigneeProfiles(newTasks);

        setTasks((prev) => {
          const existingIds = new Set(prev.map((t) => t.id));
          const hydratedExisting = prev.map(hydrateTaskAssignee);
          const filtered = newTasks
            .filter((t) => !existingIds.has(t.id))
            .map(hydrateTaskAssignee);
          return [...hydratedExisting, ...filtered];
        });

        pageRef.current[status] = page + 1;

        setHasMoreByStatus((prev) => ({
          ...prev,
          [status]: !res.data.last,
        }));
      } catch (err) {
        console.error("Failed fetching tasks:", err);
      } finally {
        loadingRef.current[status] = false;
        setLoadingByStatus((prev) => ({ ...prev, [status]: false }));
      }
    },
    [projectId, iterationId, refetchAssigneeProfiles, hydrateTaskAssignee],
  );

  const reset = useCallback(() => {
    setTasks([]);
    setPageByStatus({});
    setHasMoreByStatus({});
    setLoadingByStatus({});
    pageRef.current = {};
    loadingRef.current = {};
    assigneeCacheRef.current = new Map();
  }, []);

  useEffect(() => {
    if (!projectId) return;
    reset();
  }, [projectId, iterationId, reset]);

  return {
    tasks,
    setTasks,
    fetchTasksForColumn,
    hasMoreByStatus,
    loadingByStatus,
    resetTasks: reset,
  };
};
